/**
 * OTIF Exclusions Tab — select schedule-order POs that should NOT be counted
 * in OTIF performance calculations. Excluded POs still show their schedule
 * orders, forecast and deliveries everywhere else — only OTIF skips them.
 */
import { useEffect, useRef, useState } from 'react';
import { pageCountFromResponse, type PageSizeMemory } from '../../../hooks/usePagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Switch } from '../../ui/switch';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Ban, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

import { purchasingAPI, type OTIFExclusionRow } from '../../../lib/api';
import { OTIFShutdownWeeksCard } from './OTIFShutdownWeeksCard';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/** Sortable columns → backend ordering field. */
type SortField = 'po_number' | 'supplier__name' | 'schedule_count' | 'exclude_from_otif'
  | 'erp_order_type';

/** Raw ERP order-type codes are opaque on their own — 4 is a scheduling
 *  agreement and 1 a spot/one-off order, which is exactly the distinction
 *  that matters when deciding what to leave out of OTIF. Show the code with
 *  its meaning, and fall through unchanged for any code we don't recognise
 *  rather than hiding it. */
const ERP_ORDER_TYPE_LABELS: Record<string, string> = {
  '1': '1 · Spot',
  '4': '4 · Schedule',
};

function erpOrderTypeLabel(code: string | null | undefined): string {
  const raw = (code ?? '').trim();
  if (!raw) return '—';
  return ERP_ORDER_TYPE_LABELS[raw] ?? raw;
}

/** Which slice of the schedule-PO list is on screen. */
type ExclusionFilter = 'all' | 'excluded' | 'included';

export function OTIFExclusionsTab() {
  const [rows, setRows] = useState<OTIFExclusionRow[]>([]);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [ordering, setOrdering] = useState<string>('po_number');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // Excluded POs are a handful among thousands, so without this the only way
  // to review them was to page through the whole list looking for lit toggles.
  const [filter, setFilter] = useState<ExclusionFilter>('all');
  const [excludedCount, setExcludedCount] = useState<number | null>(null);

  // Server-side search, sort + pagination (thousands of schedule POs).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await purchasingAPI.getOTIFExclusions({
          page,
          page_size: pageSize,
          ordering,
          ...(search.trim() ? { search: search.trim() } : {}),
          // Backend already exposes exclude_from_otif via filterset_fields.
          ...(filter === 'all' ? {} : { exclude_from_otif: filter === 'excluded' }),
        });
        if (!alive) return;
        setRows(r.data.results || []);
        setCount(r.data.count || 0);
        // CR-7279: page count from what the server served, not what was asked for.
        const paged = pageCountFromResponse(r.data.count || 0, pageSize, (r.data.results || []).length, page, servedPageRef.current);
        servedPageRef.current = paged.memory;
        setTotalPages(Math.max(1, paged.totalPages));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to load schedule orders';
        toast.error(msg);
      } finally {
        if (alive) setLoading(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [search, page, pageSize, ordering, filter]);

  // Badge count for the Excluded tab. Kept as its own tiny request (page_size=1,
  // read `count` only) so it stays correct while the main list is filtered or
  // searched. `refreshKey` re-runs it after a toggle, since flipping a row
  // changes the total.
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    let alive = true;
    purchasingAPI
      .getOTIFExclusions({ page_size: 1, exclude_from_otif: true })
      .then((r) => { if (alive) setExcludedCount(r.data.count || 0); })
      .catch(() => { if (alive) setExcludedCount(null); });
    return () => { alive = false; };
  }, [refreshKey]);

  const onSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const onSort = (field: SortField) => {
    setOrdering((prev) => (prev === field ? `-${field}` : prev === `-${field}` ? field : field));
    setPage(1);
  };

  const onPageSize = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const toggle = async (row: OTIFExclusionRow, next: boolean) => {
    setSavingId(row.id);
    try {
      await purchasingAPI.setOTIFExclusion(row.id, next);
      if (filter === 'all') {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, exclude_from_otif: next } : r)));
      } else {
        // On a filtered tab the row no longer belongs here — drop it rather than
        // leaving a row that contradicts the tab it is sitting under.
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        setCount((prev) => Math.max(0, prev - 1));
      }
      setRefreshKey((k) => k + 1);
      toast.success(`${row.po_number} ${next ? 'excluded from' : 'included in'} OTIF`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update exclusion';
      toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  const SortHeader = ({ field, label, className }: { field: SortField; label: string; className?: string }) => {
    const active = ordering === field || ordering === `-${field}`;
    const Icon = !active ? ChevronsUpDown : ordering.startsWith('-') ? ChevronDown : ChevronUp;
    return (
      <TableHead className={className}>
        <button
          type="button"
          onClick={() => onSort(field)}
          className="inline-flex items-center gap-1 hover:text-foreground font-medium"
        >
          {label}
          <Icon className={`h-3.5 w-3.5 ${active ? 'text-foreground' : 'text-muted-foreground/50'}`} />
        </button>
      </TableHead>
    );
  };

  const Pagination = () => (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
      <div className="flex items-center gap-2">
        <span>Per page:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="h-8 rounded-md border bg-background px-2 text-foreground"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span className="ml-2">
          {count.toLocaleString()} schedule order{count === 1 ? '' : 's'}
          {count > 0 && ` · page ${page} of ${totalPages}`}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={loading || page <= 1}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={loading || page >= totalPages}>
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Both cards keep work out of OTIF, at different grains: whole weeks the
          site was closed, and individual POs that should never count. */}
      <OTIFShutdownWeeksCard />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5" />
            OTIF Exclusions
          </CardTitle>
          <CardDescription>
            Choose which schedule-order POs are left out of OTIF calculations. Excluded POs still
            appear under schedule orders, forecast and deliveries — only the OTIF % ignores them.
            Search by PO number, supplier or part number.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search PO number, supplier or part…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Excluded POs are a small handful among thousands, so reviewing
              "what have we excluded?" previously meant paging the whole list
              hunting for lit toggles. These tabs filter server-side. */}
          <div className="flex flex-wrap items-center gap-2">
            {([
              { key: 'all', label: 'All schedule orders', badge: null },
              { key: 'excluded', label: 'Excluded from OTIF', badge: excludedCount },
              { key: 'included', label: 'Included in OTIF', badge: null },
            ] as const).map((tab) => (
              <Button
                key={tab.key}
                type="button"
                size="sm"
                variant={filter === tab.key ? 'default' : 'outline'}
                onClick={() => { setFilter(tab.key); setPage(1); }}
              >
                {tab.label}
                {tab.badge != null && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                      filter === tab.key ? 'bg-primary-foreground/20' : 'bg-muted'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </Button>
            ))}
          </div>

          <Pagination />

          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading schedule orders…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No schedule orders found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader field="po_number" label="PO Number" />
                  <SortHeader field="supplier__name" label="Supplier" />
                  <SortHeader field="erp_order_type" label="ERP Order Type" />
                  <TableHead>Part Numbers</TableHead>
                  <SortHeader field="schedule_count" label="Schedules" className="text-center" />
                  <SortHeader field="exclude_from_otif" label="Exclude from OTIF" className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.po_number}</TableCell>
                    <TableCell>{row.supplier_name ?? '—'}</TableCell>
                    <TableCell>{erpOrderTypeLabel(row.erp_order_type)}</TableCell>
                    <TableCell className="max-w-[280px] truncate" title={row.part_numbers ?? ''}>
                      {row.part_numbers || '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{row.schedule_count ?? 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2">
                        {savingId === row.id && (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                        <Switch
                          checked={row.exclude_from_otif}
                          onCheckedChange={(checked) => toggle(row, checked)}
                          disabled={savingId === row.id}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination />
        </CardContent>
      </Card>
    </div>
  );
}
